import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Joyride, STATUS, ACTIONS } from "react-joyride";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LS_KEY = "mag_onboarding_done";

/**
 * Helper: build the list of tour steps based on whether the user is owner.
 * Steps are written in Italian and use a friendly, "first 60 seconds" tone.
 */
function buildSteps(isOwner) {
  const steps = [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: "Benvenuto in InPalmo",
      content:
        "Ti facciamo fare un giro veloce (meno di 1 minuto). Abbiamo già caricato qualche prodotto demo, così l'app ti sembrerà subito viva. Puoi cancellarli quando vuoi.",
    },
    {
      target: '[data-testid="insights-section"]',
      placement: "bottom",
      title: "La Dashboard",
      content:
        "Qui vedi a colpo d'occhio gli avvisi: prodotti in scadenza, sotto scorta o fermi da troppo tempo. Le card e i grafici sono cliccabili per andare al dettaglio.",
    },
    {
      target: '[data-testid="nav-products"]',
      placement: "right",
      title: "Prodotti",
      content:
        "L'inventario completo del negozio. Puoi aggiungerli a mano, importarli da file Excel/CSV o lasciar fare all'AI partendo da una foto.",
    },
    {
      target: '[data-testid="nav-products"]',
      placement: "right",
      title: "Scansione AI (la chicca!)",
      content:
        "Dentro la pagina Prodotti, il pulsante 'Scansiona' permette di fotografare uno scontrino o un DDT: l'AI estrae prodotti, prezzi, quantità e sconti. Funziona anche con più foto in batch.",
    },
    {
      target: '[data-testid="nav-sales"]',
      placement: "right",
      title: "Vendite",
      content:
        "Registra qui le vendite del giorno. Anche da foto dello scontrino! Lo stock dei prodotti viene aggiornato automaticamente.",
    },
    {
      target: '[data-testid="nav-suppliers"]',
      placement: "right",
      title: "Fornitori",
      content:
        "Rubrica dei fornitori che si auto-impara: ogni volta che scansioni un DDT, l'AI suggerisce e arricchisce la scheda fornitore (sconto medio, articoli ricorrenti, ecc.).",
    },
  ];

  if (isOwner) {
    steps.push({
      target: '[data-testid="nav-team"]',
      placement: "right",
      title: "Team",
      content:
        "Puoi invitare cassieri o collaboratori con un codice. Decidi tu cosa possono fare (vedere vendite, gestire prodotti, ecc.).",
    });
  }

  steps.push(
    {
      target: '[data-testid="nav-analytics"]',
      placement: "right",
      title: "Analisi",
      content:
        "Trend mensili, top prodotti, margini. È la sezione che ti aiuta a capire cosa funziona e cosa no nel negozio.",
    },
    {
      target: '[data-testid="nav-settings"]',
      placement: "right",
      title: "Impostazioni",
      content:
        "Da qui configuri notifiche email/in-app, soglie di scadenza e — importante — scegli se mostrare o nascondere il calcolo dei margini sui prodotti. Vuoi usare InPalmo solo per scadenze e giacenze? Disattiva il margine e l'app diventa essenziale.",
    },
    {
      target: "body",
      placement: "center",
      title: "Pronto a partire!",
      content:
        "Hai finito! Quando vuoi togliere i dati demo, vai in Impostazioni → 'Reset dati demo' oppure usa il banner di benvenuto sulla Dashboard. Buon lavoro!",
    }
  );

  return steps;
}

const joyrideStyles = {
  options: {
    primaryColor: "#2E4A3B",
    textColor: "#1A1C1A",
    backgroundColor: "#FFFFFF",
    arrowColor: "#FFFFFF",
    overlayColor: "rgba(26, 28, 26, 0.55)",
    zIndex: 10000,
    width: 380,
  },
  tooltip: {
    borderRadius: 14,
    padding: 20,
    fontFamily: "Inter, sans-serif",
  },
  tooltipTitle: {
    fontFamily: "Work Sans, sans-serif",
    fontSize: 16,
    fontWeight: 600,
    margin: 0,
    paddingBottom: 8,
  },
  tooltipContent: {
    fontSize: 14,
    lineHeight: 1.55,
    color: "#5C5F5A",
    padding: 0,
  },
  buttonNext: {
    backgroundColor: "#2E4A3B",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
  },
  buttonBack: {
    color: "#5C5F5A",
    fontSize: 13,
    marginRight: 8,
  },
  buttonSkip: {
    color: "#8E918C",
    fontSize: 13,
  },
  buttonClose: {
    display: "none",
  },
};

const localeIt = {
  back: "Indietro",
  close: "Chiudi",
  last: "Fine",
  next: "Avanti",
  open: "Apri il tour",
  skip: "Salta",
};

export default function OnboardingTour() {
  const { user, team, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const decidedRef = useRef(false); // avoid re-running decision logic multiple times

  const isOwner = team?.role === "owner" && !team?.is_guest;
  const steps = buildSteps(isOwner);

  // Decide whether to start the tour after auth + team are loaded.
  useEffect(() => {
    if (loading || decidedRef.current) return;
    // We allow the tour for both logged-in users and guests in demo mode.
    // For guests we use only localStorage (backend treats guests as completed).
    decidedRef.current = true;

    (async () => {
      // Local override (e.g. user clicked "Rivedi tour" in Settings)
      let forceShow = false;
      try {
        forceShow = localStorage.getItem("mag_onboarding_force") === "1";
        if (forceShow) localStorage.removeItem("mag_onboarding_force");
      } catch (_e) { /* noop */ }

      let completed = false;
      try {
        if (user) {
          const status = await api.onboardingStatus();
          completed = !!status.completed;
        } else {
          // Guest: use localStorage as the only marker
          completed = localStorage.getItem(LS_KEY) === "1";
        }
      } catch (_e) {
        try {
          completed = localStorage.getItem(LS_KEY) === "1";
        } catch (_e2) { /* noop */ }
      }

      if (forceShow || !completed) {
        // Make sure the tour starts from the Dashboard so targets are visible
        if (!location.pathname.startsWith("/app/dashboard")) {
          navigate("/app/dashboard", { replace: true });
        }
        setTimeout(() => {
          setRun(true);
        }, 600);
      } else {
        try { localStorage.setItem(LS_KEY, "1"); } catch (_e) { /* noop */ }
      }
    })();
  }, [loading, user, navigate, location.pathname]);

  const finishTour = useCallback(async () => {
    setRun(false);
    try { localStorage.setItem(LS_KEY, "1"); } catch (_e) { /* noop */ }
    try { await api.onboardingComplete(); } catch (_e) { /* noop */ }
  }, []);

  const handleCallback = useCallback(
    (data) => {
      const { status, action } = data;
      const finished = [STATUS.FINISHED, STATUS.SKIPPED].includes(status);
      if (finished) {
        finishTour();
        return;
      }
      if (action === ACTIONS.CLOSE) {
        finishTour();
      }
    },
    [finishTour]
  );

  // Don't render anything while auth is loading
  if (loading) return null;

  return (
    <div data-testid="onboarding-tour">
      <Joyride
        steps={steps}
        run={run}
        continuous
        showSkipButton
        showProgress
        hideCloseButton={false}
        disableScrolling={false}
        disableOverlayClose
        scrollToFirstStep
        spotlightPadding={6}
        locale={localeIt}
        styles={joyrideStyles}
        onEvent={handleCallback}
        floaterProps={{ disableAnimation: false }}
      />
    </div>
  );
}
